import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCsv } from "@fortawesome/free-solid-svg-icons";
import { Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import MyModal from "../Common/MyModal";
import {
  AssetExportFormat,
  AssetExportLocationFormat,
  AssetExportRequest,
  useExportAssetsMutation,
} from "../store/slices/mapsSlice";
import { downloadWorkflowFile } from "../proxyAPI";

type ExportAssetsModalProps = {
  show: boolean;
  onHide: () => void;
  filters: AssetExportRequest;
};

const defaultExportFilename = () => {
  const pad = (value: number) => String(value).padStart(2, "0");
  const now = new Date();
  return `assets export ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};

export default function ExportAssetsModal({ show, onHide, filters }: ExportAssetsModalProps) {
  const [filename, setFilename] = useState(defaultExportFilename);
  const [format, setFormat] = useState<AssetExportFormat>("csv");
  const [exportContacts, setExportContacts] = useState(true);
  const [exportRelationships, setExportRelationships] = useState(true);
  const [locationFormat, setLocationFormat] = useState<AssetExportLocationFormat>("lng_lat");
  const [createdFileUuid, setCreatedFileUuid] = useState("");
  const [createdFilename, setCreatedFilename] = useState("");
  const [exportStatus, setExportStatus] = useState("");
  const [exportedRows, setExportedRows] = useState(0);
  const [exportAssets, exportState] = useExportAssetsMutation();

  useEffect(() => {
    if (!show) {
      setFilename(defaultExportFilename());
      setFormat("csv");
      setExportContacts(true);
      setExportRelationships(true);
      setLocationFormat("lng_lat");
      setCreatedFileUuid("");
      setCreatedFilename("");
      setExportStatus("");
      setExportedRows(0);
    }
  }, [show]);

  const handleExport = async () => {
    const response: any = await exportAssets({
      ...filters,
      filename: filename.trim(),
      format,
      export_contacts: exportContacts,
      export_relationships: exportRelationships,
      location_format: locationFormat,
    });
    if (response.error) {
      toast.error("Asset export failed");
      return;
    }

    const file = response.data?.file;
    if (!file?.uuid) {
      toast.error("Asset export file could not be created");
      return;
    }

    setCreatedFileUuid(file.uuid);
    setCreatedFilename(file.filename || "asset-export.csv");
    setExportStatus(response.data?.session?.status || file.asset_export?.status || "queued");
    setExportedRows(response.data?.rows || 0);
    toast.success("Asset export queued");
  };

  return (
    <MyModal
      show={show}
      onHide={onHide}
      size="md"
      heading={
        <>
          <FontAwesomeIcon icon={faFileCsv} className="me-2" />
          Export assets
        </>
      }
    >
      <div className="d-flex flex-column gap-3">
        <div className="alert alert-info mb-0">
          Export files are created in the Document Library with the <strong>assets export</strong> tag.
        </div>
        <Form.Group>
          <Form.Label>File name</Form.Label>
          <Form.Control
            value={filename}
            onChange={(event) => setFilename(event.target.value)}
            placeholder="assets export YYYY-MM-DD HH:MM:SS"
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Format</Form.Label>
          <Form.Select
            value={format}
            onChange={(event) => setFormat(event.target.value as AssetExportFormat)}
          >
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
          </Form.Select>
        </Form.Group>
        <Form.Check
          type="checkbox"
          id="asset-export-contacts"
          label="Include contacts"
          checked={exportContacts}
          onChange={(event) => setExportContacts(event.target.checked)}
        />
        <Form.Check
          type="checkbox"
          id="asset-export-relationships"
          label="Include parent and child references"
          checked={exportRelationships}
          onChange={(event) => setExportRelationships(event.target.checked)}
        />
        <Form.Group>
          <Form.Label>Location column</Form.Label>
          <Form.Select
            value={locationFormat}
            onChange={(event) => setLocationFormat(event.target.value as AssetExportLocationFormat)}
          >
            <option value="none">Do not include locations</option>
            <option value="geojson">GeoJSON</option>
            <option value="lat_lng">LatLng</option>
            <option value="lng_lat">LngLat</option>
          </Form.Select>
        </Form.Group>
        {createdFileUuid && (
          <div className="alert alert-success mb-0">
            <div className="fw-bold">{createdFilename}</div>
            <div>
              Export queued in the Document Library
              {exportStatus ? ` (${exportStatus})` : ""}.
              {exportedRows > 0 ? ` ${exportedRows} asset rows exported.` : " The file will be available when processing completes."}
            </div>
            <div className="d-flex flex-wrap gap-3 mt-2">
              <Link to="/library/document">Open Document Library</Link>
              <Link to={`/library/document?file=${createdFileUuid}`}>
                Direct file link
              </Link>
            </div>
          </div>
        )}
        <div className="d-flex justify-content-end gap-2 pt-3 border-top">
        <Button variant="secondary" onClick={onHide} disabled={exportState.isLoading}>
          Close
        </Button>
        {createdFileUuid && (
          <Button
            variant="outline-primary"
            onClick={() => downloadWorkflowFile(createdFileUuid, {}, createdFilename)}
          >
            Try download
          </Button>
        )}
        <Button variant="primary" onClick={handleExport} disabled={exportState.isLoading}>
          {exportState.isLoading && <Spinner animation="border" size="sm" className="me-2" />}
          Create export
        </Button>
        </div>
      </div>
    </MyModal>
  );
}